import { ArrowLeft, Mail, Phone, Instagram } from "lucide-react";

const GALLERY = [
  {
    url: "/assets/directors/aharon-family.jpg",
    alt: "Aharon Shepard with his family — House Shepard, the frontier portrait",
    caption: "House Shepard — the family, the legacy",
    anchor: "object-cover",
  },
  {
    url: "/assets/directors/aharon-gym-hylo.jpg",
    alt: "Aharon Shepard in the weight room, HYLO training shirt",
    caption: "In the weight room — the treasury keeps a training floor too",
    anchor: "object-top",
  },
  {
    url: "/assets/directors/aharon-gym-fruits.jpg",
    alt: "Aharon Shepard in a cowboy hat and FRUITS tee at the gym",
    caption: "The hat stays on — discipline with character",
    anchor: "object-top",
  },
  {
    url: "/assets/cards/founding-proofs/front-aharon-shepard.png",
    alt: "Aharon Shepard's founding card — the golden bear seal of the Institute",
    caption: "Founding proof — the golden bear seal",
  },
];

const FILMS = [
  {
    url: "/assets/directors/aharon-film-1.mp4",
    poster: "/assets/directors/aharon_poster_1.jpg",
    caption: "The work — in motion",
  },
  {
    url: "/assets/directors/aharon-film-2.mp4",
    poster: "/assets/directors/aharon_poster_2.jpg",
    caption: "The desk — in motion",
  },
  {
    url: "/assets/directors/aharon-film-3.mp4",
    poster: "/assets/directors/aharon_poster_3.jpg",
    caption: "The house — in motion",
  },
];

const DirectorAharon = () => {
  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <div className="max-w-5xl mx-auto px-6 py-12 space-y-16">
        <a
          href="/#directors"
          data-testid="aharon-back-link"
          className="inline-flex items-center gap-2 text-xs text-stone-400 hover:text-amber-400 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Directorship
        </a>

        {/* Hero */}
        <div className="grid md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-2 flex justify-center md:justify-start">
            <div
              className="w-44 sm:w-52 rounded-xl overflow-hidden border border-amber-500/30 shadow-[0_0_40px_rgba(245,158,11,0.10)]"
              data-testid="aharon-portrait"
            >
              <img
                src="/assets/directors/aharon-shepard-plaid.webp"
                alt="Aharon 'J.D.' Shepard — Head of Treasury Management, in the house plaid"
                className="w-full h-[420px] object-cover object-[50%_20%]"
              />
            </div>
          </div>

          <div className="md:col-span-3 space-y-8">
            <div className="space-y-3">
              <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
                Managing Directorship
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight display-serif">
                Aharon "J.D." Shepard
              </h1>
              <p className="text-stone-300 text-base md:text-lg">
                Head of Treasury Management / International Financial Officer
              </p>
              <p className="text-xs text-stone-500">The Bourg Merchant Exchange</p>
              <a
                href="https://instagram.com/alkaline_thecowboy"
                target="_blank"
                rel="noreferrer"
                data-testid="aharon-instagram-link"
                className="inline-flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors pt-1"
              >
                <Instagram size={14} /> @alkaline_thecowboy — Aharón from House Shepard
              </a>
            </div>

            <blockquote
              data-testid="aharon-quote"
              className="border-l-2 border-amber-500/60 pl-5 py-1 text-stone-200 text-base md:text-lg italic display-serif"
            >
              "God's Law, paterfamilias — the house holds its own ledger."
            </blockquote>

            <div className="space-y-4" data-testid="aharon-disciplines">
              <div className="border-b border-stone-800/70 pb-3">
                <p className="mono-tag text-[10px] uppercase tracking-widest text-stone-500">Discipline</p>
                <p className="text-sm text-stone-200 mt-1">Financial Engineer / Monetary Scientist</p>
              </div>
              <div className="border-b border-stone-800/70 pb-3">
                <p className="mono-tag text-[10px] uppercase tracking-widest text-stone-500">Markets</p>
                <p className="text-sm text-stone-200 mt-1">Lex Mercatoria — capital market &amp; debt capital market</p>
              </div>
            </div>

            <div className="space-y-4" data-testid="aharon-credentials">
              <div className="border-b border-stone-800/70 pb-3">
                <p className="mono-tag text-[10px] uppercase tracking-widest text-stone-500">Office</p>
                <p className="text-sm text-stone-200 mt-1">
                  Treasury management and international finance for the Exchange — the house's
                  reserves, the member ledger's integrity, and Chain 9940 settlement discipline
                </p>
              </div>
              <div className="border-b border-stone-800/70 pb-3">
                <p className="mono-tag text-[10px] uppercase tracking-widest text-stone-500">Desk</p>
                <p className="text-sm text-stone-200 mt-1">The Bourg Merchant Exchange — Henderson &amp; Las Vegas, Nevada</p>
              </div>
            </div>

            <div className="rounded-xl bg-stone-900/60 border border-stone-800 p-5 space-y-2" data-testid="aharon-contact">
              <p className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">Treasury Desk — Direct</p>
              <a href="tel:+17252765988" className="flex items-center gap-2 text-sm text-stone-200 hover:text-amber-300 transition-colors">
                <Phone size={14} className="text-amber-500" /> (725) 276-5988
              </a>
              <a href="mailto:jd.treasurymgmt@brocointernational.com" className="flex items-center gap-2 text-sm text-stone-200 hover:text-amber-300 transition-colors break-all">
                <Mail size={14} className="text-amber-500" /> jd.treasurymgmt@brocointernational.com
              </a>
            </div>
          </div>
        </div>

        {/* Film Room */}
        <section className="space-y-6" data-testid="aharon-film-room">
          <div className="max-w-2xl space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              In Motion
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight display-serif">
              The Work, in Motion
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {FILMS.map((film, i) => (
              <figure
                key={film.url}
                data-testid={`aharon-film-${i}`}
                className="rounded-xl overflow-hidden bg-stone-900/60 border border-stone-800"
              >
                <video
                  controls
                  preload="metadata"
                  poster={film.poster}
                  className="w-full aspect-video object-cover"
                >
                  <source src={film.url} type="video/mp4" />
                </video>
                <figcaption className="px-4 py-3 text-xs text-stone-400">{film.caption}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Credentials */}
        <section className="space-y-6" data-testid="aharon-credentials-section">
          <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
            Credentials
          </span>
          <div className="rounded-xl overflow-hidden border border-amber-500/30 bg-stone-950 max-w-2xl shadow-[0_0_40px_rgba(245,158,11,0.08)]">
            <img
              src="/assets/private/aharon-broco-license.pdf"
              alt="Aharon Shepard — Certificate of Corporate License, Broco International"
              className="w-full h-auto"
            />
          </div>
        </section>

        {/* Gallery */}
        <section className="space-y-6" data-testid="aharon-gallery">
          <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
            Off the Ledger
          </span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {GALLERY.map((shot) => (
              <figure
                key={shot.url}
                className="rounded-xl overflow-hidden bg-stone-900/60 border border-stone-800 group"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={shot.url}
                    alt={shot.alt}
                    className={`w-full h-full object-cover object-[50%_18%] group-hover:scale-105 transition-transform duration-700`}
                    loading="lazy"
                  />
                </div>
                <figcaption className="px-4 py-3 text-xs text-stone-400">{shot.caption}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DirectorAharon;
